import { Component, OnInit, Input, SimpleChanges, Output, EventEmitter, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    IonGrid, IonRow, IonCol, IonSpinner, ToastController, AlertController
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import moment from 'moment';

import { 
    MarvButtonComponent, MarvSelectItem, MarvSelectComponent, MarvInputDateComponent, MarvInputTimeComponent, MarvTextareaComponent, MarvToggleComponent
} from '@marv/components';
import { MarvValidatorsService, MarvDateService } from '@marv/services';
import { PermanentAgentService, PermanentPlaningEventService, PermanentPlanningService } from '@app/services';
import { PearlsPlanningEvent, PearlsPlanningEventDto } from '@app/models';

export class PermanentPlanningEvent {
    id: number;
    startDate: Date;
    startHour: string;
    duration: number;
    allDay: boolean;
    eventType: number;
    description: string;
    agentsId: Array<number>;
    
    toPearlsPlanningEventDto(): PearlsPlanningEventDto {
        let e = new PearlsPlanningEventDto();
        let refDate = MarvDateService.FromDate(this.startDate);
        if(refDate){
            let start = moment(this.startDate);
            let time = this.startHour.split(':');
            if(time.length >= 2){
                start.hours(parseInt(time[0]));
                start.minutes(parseInt(time[1]));
            }
            e.id = this.id;
            e.start = start.toDate().getTime();
            e.duration = this.duration;
            e.refDate = refDate;
            e.description = this.description;
            e.salesreps = this.agentsId;
            e.fullDay = this.allDay;
            e.eventsType = this.eventType;
        }
        return e;
    }
    
    static FromForm(form: FormGroup, idEvent:number|null = null): PermanentPlanningEvent{
        let e = new PermanentPlanningEvent();
                
        e.id = (idEvent ? idEvent : -1);
                
        if(form.value?.agentsId){
            e.agentsId = form.value.agentsId;
        }
        
        if(form.value?.startDate){
            e.startDate = form.value.startDate;
        }
        
        if(form.value?.startHour){
            e.startHour = form.value.startHour;
        }
        
        if(form.value?.duration){
            e.duration = form.value.duration;
        }
        
        e.allDay = (form.value?.allDay ? true : false);
        
        if(form.value?.eventType){
            e.eventType = form.value.eventType;
        }
        
        if(form.value?.description){
            e.description = form.value.description;
        }
        
        return e;
    }
}

@Component({
    selector: 'app-permanent-planning-event',
    templateUrl: './event.component.html',
    styleUrls: ['./event.component.scss'],
    imports: [
        IonGrid, IonRow, IonCol, IonSpinner, TranslatePipe, ReactiveFormsModule, 
        forwardRef(() => MarvButtonComponent), forwardRef(() => MarvSelectComponent), forwardRef(() => MarvInputDateComponent),
        forwardRef(() => MarvInputTimeComponent), forwardRef(() => MarvTextareaComponent), forwardRef(() => MarvToggleComponent)
    ]
})
export class PermanentPlanningEventComponent  implements OnInit {

    @Input({alias : 'operationId', required : true}) operationId: number;
    @Input({alias : 'agentId', required : false}) agentId: number;
    @Input({alias : 'eventId', required : false}) eventId: number|null = null;
    @Output() onChangePermanentPlanningEvent = new EventEmitter<PermanentPlanningEvent|null>();
    @Output() onDeletePermanentPlanningEvent = new EventEmitter<boolean>();
        
    isFetching: boolean = false; 
    isFetchingEvent: boolean = false; 
    isFetchingSave: boolean = false; 
    isFetchingDelete: boolean = false; 
    event: PearlsPlanningEvent|null = null;
    permanentPlanningEventForm: FormGroup = new FormGroup({
        agentsId: new FormControl([], {nonNullable: true, validators: [Validators.required]}),
        startDate: new FormControl(null, [Validators.required]),
        startHour: new FormControl(null, [Validators.required]),
        duration: new FormControl(null, [Validators.required]),
        allDay: new FormControl(false, {nonNullable: true}),
        eventType: new FormControl(null, [Validators.required]),
        description: new FormControl(null),
    });  
    agentsItems: Array<MarvSelectItem> = [];
    durations: Array<MarvSelectItem> = [];   
    eventsTypes: Array<MarvSelectItem> = [];  

    constructor(
        public translateService: TranslateService,
        private toastController: ToastController,
        private alertController: AlertController,
        private permanentAgentService: PermanentAgentService,
        private permanentPlanningService: PermanentPlanningService,
        private permanentPlaningEventService: PermanentPlaningEventService
    ) { }

    ngOnInit() {
        
        for(let i=0;i<=7;i++){
            if(i>0){
                this.durations.push(new MarvSelectItem(i+"H00", i*60));
            }  
            if(i<7){
                this.durations.push(new MarvSelectItem(i+"H30", (i*60)+30));
            }                      
        }
        
    }
    
    async ngOnChanges(changes: SimpleChanges) {
        if(changes['operationId']){
            this.isFetching = true;
            let agentsItems: Array<MarvSelectItem> = [];
            if(this.operationId){
                let agents = await this.permanentAgentService.loadPermanentsAgents(this.operationId);
                for(let agent of agents){
                    agentsItems.push(new MarvSelectItem(agent.name,agent.id));
                }
            }
            this.agentsItems = agentsItems;
            
            let eventsTypes: Array<MarvSelectItem> = [];   
            if(MarvValidatorsService.isInteger(this.operationId)){ 
                let types = await this.permanentPlaningEventService.loadEventsType(this.operationId);
                types.forEach(t => {
                    eventsTypes.push(new MarvSelectItem(t.name, t.id));
                });
            }
            this.eventsTypes = eventsTypes;
            this.isFetching = false;
        }
        
        if(changes['eventId']){
            this.isFetchingEvent = true;
            let event: PearlsPlanningEvent|null = null;
            if(this.eventId){                
                event = await this.permanentPlaningEventService.loadEvent(this.operationId, this.eventId);                
            }
            
            if(event){
                let values = {
                    agentsId: event.salesreps,
                    startDate: event.startDate,
                    startHour: event.startHour,
                    duration: event.duration,
                    allDay: event.allDay,
                    eventType: event.eventType,
                    description: event.description,
                }
                this.permanentPlanningEventForm.setValue(values);
            }
            else{
                this.permanentPlanningEventForm.reset();
                this.permanentPlanningEventForm.controls['agentsId'].setValue((this.agentId ? [this.agentId] : []), {emitEvent: false});
            }
            
            this.event = event;
            this.isFetchingEvent = false;
        }
        
        if(changes['agentId']){
            this.permanentPlanningEventForm.controls['agentsId'].setValue((this.agentId ? [this.agentId] : []), {emitEvent: false});
        }
        
    }

    async saveEvent() {
        if(this.permanentPlanningEventForm.valid){            
            this.isFetchingSave = true;
            let status = false;
            let event = PermanentPlanningEvent.FromForm(this.permanentPlanningEventForm, this.eventId);
            if(event.id && event.id !== -1){
                status = await this.permanentPlaningEventService.updateEvent(this.operationId, event.toPearlsPlanningEventDto());
            }
            else{
                status = await this.permanentPlaningEventService.createEvent(this.operationId, event.toPearlsPlanningEventDto());
            }
            if(status){
                this.onChangePermanentPlanningEvent.emit(event);                
                this.permanentPlanningEventForm.reset();
                const toast = await this.toastController.create({
                    message: (event.id ? this.translateService.instant('MSG_SUCCESS_EVENT_UPDATE') : this.translateService.instant('MSG_SUCCESS_EVENT_CREATE')),
                    duration: 6000,
                    position: 'top',
                    color: 'success'
                });
                await toast.present();
            }
            else{
                const toast = await this.toastController.create({
                    message: (event.id ? this.translateService.instant('MSG_ERROR_EVENT_UPDATE') : this.translateService.instant('MSG_ERROR_EVENT_CREATE')),
                    duration: 6000,
                    position: 'top',
                    color: 'danger'
                });
                await toast.present();
            }
            this.isFetchingSave = false;
        }
        else{
            for (var controlName in this.permanentPlanningEventForm.controls) {
                if(!this.permanentPlanningEventForm.controls[controlName].valid) {
                    this.permanentPlanningEventForm.controls[controlName].markAsTouched();
                }
            }
        }
    }
        
    async confirmDelete(){
        const alert = await this.alertController.create({
            message: this.translateService.instant('MSG_CONFIRM_DELETE_EVENT'),
            buttons: [{
                    text: this.translateService.instant('VALIDATE'),
                    role: 'confirm',
                    handler: () => {                        
                        this.delete();                        
                    }
                },
                {
                    text: this.translateService.instant('CANCEL'),
                    role: 'cancel'
                }
            ],
        })
        await alert.present();            
    }
        
    async delete(){
        if(this.event){
            this.isFetchingDelete = true;
            let statusDelete = await this.permanentPlanningService.deleteSalesRepEvent(this.operationId, this.event.id);
            if(statusDelete){
                this.onDeletePermanentPlanningEvent.emit(true);                
                this.permanentPlanningEventForm.reset();
                const toast = await this.toastController.create({
                    message: this.translateService.instant('MSG_SUCCESS_EVENT_DELETE'),
                    duration: 6000,
                    position: 'top',
                    color: 'success'
                });
                await toast.present();
            }
            else{
                const toast = await this.toastController.create({
                    message: this.translateService.instant('MSG_ERROR_EVENT_DELETE'),
                    duration: 6000,
                    position: 'top',
                    color: 'danger'
                });
                await toast.present();
            }
            this.isFetchingDelete = false;
        }
    }
    
    cancel() {
        this.onChangePermanentPlanningEvent.emit(null);
        this.permanentPlanningEventForm.reset();
    }
    
}
